import * as React from 'react';
import { useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import Button from '@mui/material/Button';
import { IRegionDefaultFormValues, IRegionFormValues, regionFormSchema } from '../../../../@types/model/masterData/region/regionFormValues';
import { useAppSelector } from '../../../../@types/redux';
import { PillButton, ControlledTextField, ControlledMuiAutoCompleteSelect } from '@zz2/zz2-ui';
import { ControlledLabelledCheckBox } from '../../../../components/input/form/ControlledLabelledCheckBoxWrapper';
import { IOptionType } from '../../../../@types/helper';
import CustomReactHookFormDevTools from '../../../../components/devtools/ReactHookFormDevTools';

interface IRegionFormProps {
    isLoading : boolean;
    initialValues : IRegionFormValues | IRegionDefaultFormValues;
    onSubmit : (values : IRegionFormValues) => void;
    onClose : () => void;
}

const RegionForm = (props : IRegionFormProps) : React.ReactElement => {
    const schema = regionFormSchema;
    const { initialValues, onSubmit, onClose, isLoading } = props;

    const { handleSubmit, control, formState: { isValid, isDirty } } = useForm<IRegionFormValues>({
        defaultValues: initialValues,
        resolver: zodResolver(schema),
        mode: 'onChange',
    });

    const organizations = useAppSelector(state => state.masterData.organizations);

    const organizationOptions : Array<IOptionType> = useMemo(() => {
        return organizations?.filter(x => x.isActive).map(x => ({ label: `(${x.code}) ${x.name}`, value: x.id })) ?? [];
    }, [organizations]);

    return (
        <div>
            <CustomReactHookFormDevTools control={control} />
            <form onSubmit={handleSubmit(onSubmit)} className={'p20'}>
                <div className={'fdc aic'}>
                    <ControlledTextField
                        className={'mb10 wfill'}
                        name={'code'}
                        label={'Code'}
                        control={control}
                        defaultValue={initialValues.code} />
                    <ControlledTextField
                        className={'mb10 wfill'}
                        name={'name'}
                        label={'Name'}
                        control={control}
                        defaultValue={initialValues.name} />
                    <ControlledMuiAutoCompleteSelect
                        className={'p0 mb10 wfill'}
                        name={'organizations'}
                        label={'Organizations'}
                        control={control}
                        options={organizationOptions}
                        defaultValues={initialValues.organizations}
                        isMulti />
                    <div className={'fdr jcfs mb10 wfill'}>
                        <ControlledLabelledCheckBox
                            className={'p0'}
                            name={'isActive'}
                            label={'Is Active'}
                            control={control}
                            defaultValue={initialValues.isActive} />
                    </div>
                    <div className={'fdr ml10 ais jcfe pt10 pb10 w100'}>
                        <Button className={'cpd mr20'} type={'button'} onClick={onClose}>
                            CANCEL
                        </Button>
                        <PillButton
                            disabled={!isDirty || !isValid || isLoading}
                            className={'ml15 pl20 pr20 h35'}
                            text={'Save'}
                            type={'submit'}
                            color={'secondary'} />
                    </div>
                </div>
            </form>
        </div>
    );
};

export default RegionForm;
